import { QueryHookOptions, useQuery } from "@apollo/client";
import { graphql } from "~/gql";
import { GenerateProposalQuery } from "~/gql/graphql";

export const PROPOSAL_FRAGMENT = graphql(/* GraphQL */ `
  fragment ProposalParts on Proposal {
    matterId
    pricingLevel
    pricingType
    status
    clientName
    matterTitle
    includeTasks
    includeRatesInProposalOutput
    includeFeeEarnersInProposalOutput
    excludeTax
    assignedResources {
      feeEarnerLevelDescription
      feeEarnerName
      rate {
        ...PriceParts
        ...PriceRangeParts
      }
    }
    legalFeeLineItems {
      title
      fee {
        ...PriceParts
        ...PriceRangeParts
      }
      tasks {
        id
        order
        title
        phaseId
        createdAt
        updatedAt
      }
      order
    }
    legalFeeTotal {
      ...PriceParts
      ...PriceRangeParts
    }
    legalFeeTotalBeforeVisibleUplifts {
      ...PriceParts
      ...PriceRangeParts
    }
    legalFeeUplifts {
      reason
      fee {
        ...PriceParts
        ...PriceRangeParts
      }
      rate
      order
    }
    legalFeeDiscount {
      ... on PriceDiscount {
        discountRate
        discountAmount
        discountedAmount
      }
      ... on PriceRangeDiscount {
        discountRateRange {
          lower
          upper
        }
        discountAmountRange {
          ...PriceRangeParts
        }
        discountedAmountRange {
          ...PriceRangeParts
        }
      }
    }
    legalFeeAdminCharge {
      ... on PriceAdminCharge {
        adminChargeRate
        adminChargeAmount
        adminChargedAmount
      }
      ... on PriceRangeAdminCharge {
        adminChargeRate
        adminChargeAmountRange {
          ...PriceRangeParts
        }
        adminChargedAmountRange {
          ...PriceRangeParts
        }
      }
    }
    legalFeeTax {
      ...TaxParts
      ...TaxRangeParts
    }
    disbursementLineItems {
      name
      amount
    }
    disbursementsTotal {
      ...PriceParts
    }
    disbursementsTax {
      ...TaxParts
    }
    grandTotal {
      ...PriceParts
      ...PriceRangeParts
    }
    assumptions
    exclusions
    fixedLegalFeeTotal {
      ...PriceParts
    }
    fixedLegalFeeTax {
      ...TaxParts
    }
    fixedLegalFeeDiscount {
      ... on PriceDiscount {
        discountRate
        discountAmount
        discountedAmount
      }
    }
    fixedLegalFeeAdminCharge {
      ... on PriceAdminCharge {
        adminChargeRate
        adminChargeAmount
        adminChargedAmount
      }
    }
    fixedGrandTotal {
      ...PriceParts
    }
    priceOptions {
      estimatedRangeChosenLower
      estimatedRangeChosenUpper
      feeRiskLower
      feeRiskUpper
      fixedChosen
      cappedChosen
    }
    feeRiskInclude
    feeRiskCappedFeeInclude
  }
`);

export const GENERATE_PARENT_PROPOSAL = graphql(/* GraphQL */ `
  query generateParentProposal($parentId: Float!) {
    generateParentProposal(parentId: $parentId) {
      collated {
        ...ProposalParts
      }
      items {
        ...ProposalParts
      }
    }
  }
`);

export const GENERATE_PROPOSAL = graphql(/* GraphQL */ `
  query generateProposal($matterId: Float!) {
    generateProposal(matterId: $matterId) {
      matterId
      pricingLevel
      pricingType
      status
      clientName
      matterTitle
      includeTasks
      includeRatesInProposalOutput
      excludeTax
      assignedResources {
        feeEarnerLevelDescription
        feeEarnerName
        rate {
          ...PriceParts
          ...PriceRangeParts
        }
      }
      legalFeeLineItems {
        title
        fee {
          ...PriceParts
          ...PriceRangeParts
        }
        tasks {
          id
          order
          title
          phaseId
          createdAt
          updatedAt
        }
        order
      }
      legalFeeTotal {
        ...PriceParts
        ...PriceRangeParts
      }
      legalFeeTotalBeforeVisibleUplifts {
        ...PriceParts
        ...PriceRangeParts
      }
      legalFeeUplifts {
        reason
        fee {
          ...PriceParts
          ...PriceRangeParts
        }
        rate
        order
      }
      legalFeeDiscount {
        ... on PriceDiscount {
          discountRate
          discountAmount
          discountedAmount
        }
        ... on PriceRangeDiscount {
          discountRateRange {
            lower
            upper
          }
          discountAmountRange {
            ...PriceRangeParts
          }
          discountedAmountRange {
            ...PriceRangeParts
          }
        }
      }
      legalFeeAdminCharge {
        ... on PriceAdminCharge {
          adminChargeRate
          adminChargeAmount
          adminChargedAmount
        }
        ... on PriceRangeAdminCharge {
          adminChargeRate
          adminChargeAmountRange {
            ...PriceRangeParts
          }
          adminChargedAmountRange {
            ...PriceRangeParts
          }
        }
      }
      legalFeeTax {
        ...TaxParts
        ...TaxRangeParts
      }
      disbursementLineItems {
        name
        amount
      }
      disbursementsTotal {
        ...PriceParts
      }
      disbursementsTax {
        ...TaxParts
      }
      grandTotal {
        ...PriceParts
        ...PriceRangeParts
      }
      assumptions
      exclusions
      fixedLegalFeeTotal {
        ...PriceParts
      }
      fixedLegalFeeTax {
        ...TaxParts
      }
      fixedLegalFeeDiscount {
        ... on PriceDiscount {
          discountRate
          discountAmount
          discountedAmount
        }
      }
      fixedLegalFeeAdminCharge {
        ... on PriceAdminCharge {
          adminChargeRate
          adminChargeAmount
          adminChargedAmount
        }
      }
      fixedGrandTotal {
        ...PriceParts
      }
      priceOptions {
        estimatedRangeChosenLower
        estimatedRangeChosenUpper
        feeRiskLower
        feeRiskUpper
        fixedChosen
        cappedChosen
      }
      feeRiskInclude
      feeRiskCappedFeeInclude
    }
  }
`);

export type Proposal = GenerateProposalQuery;
export enum ProposalQueryFetchPolicy {
  NetworkOnly = "network-only",
  NoCache = "no-cache",
}
export function useProposalQuery(matterId: number, options?: QueryHookOptions<Proposal>) {
  return useQuery(GENERATE_PROPOSAL, {
    ...options,
    variables: { matterId },
  });
}
